/**
 * SSML チューニング画面 (`/ssml-tuning`)。
 *
 * 放送文を SSML でチューニングし、狙った聞こえ方の音声を作るための画面。
 * TTS は Amazon Polly / 話者 Takumi / `ja-JP` 固定 (`docs/ssml-tuning-plan.md` 8 章)。
 *
 * ## 本ファイルの現状 (SBI-005: Step2 Audio 操作対応)
 *
 *   - Step1 (放送テキストの入力 + プレーンテキスト Audio) … **実装済み** (SBI-001 / SBI-002)
 *   - 「辞書適用Audio作成」(Step1 → Step2)            … **実装済み** (SBI-003 / 既存 `POST /audio-preview` の `applied_ssml`)
 *   - Step2 (辞書適用済 SSML の確認・編集)             … **実装済み** (SBI-004)
 *   - Step2 の Audio 操作 (試聴 / 再作成 / ダウンロード) … **実装済み** (SBI-005 / 編集した SSML をそのまま Polly へ)
 *   - Step3 (最大 5 候補の生成 → 各候補の音声生成)     … **実装済み**
 *   - Step3 の音声再生 / 失敗候補の再試行              … **実装済み**
 *   - Step3 の SSML 編集 / Audio 再作成                … **実装済み**
 *   - Step3 の候補ごとの Audio ダウンロード            … **実装済み**
 *   - 履歴管理 (v1/v2/v3) / Undo / Readme 自動育成     … 対象外 (Phase2)
 *
 * ⚠️ SBI-001 で「読み上げ要望」「AI 改善案」「改善理由」を画面から廃止した。
 *   Step1 の放送テキストは、暫定でそのまま Step3 の入力となる (AI 解析 API は呼ばない)。
 *   `POST /ssml-tuning/analyze` 自体はバックエンド最小改修の方針で残している (画面からは未使用)。
 *
 * ⚠️ 「Audio サンプル作成」1 回で **Bedrock 1 回 + Polly を候補数と同じ回数**
 *   呼ぶ (計画 9.3.1)。音声は Amazon Polly / 話者 Takumi 固定で、
 *   Provider 選択・Voice 選択は設けない。
 *
 * Step1・Step2 はどちらも `Step1AnalyzePanel` が担う (放送テキストの state は本ページが持ち、
 * Step3 と共有する)。
 *
 * ## Step の切り方 (M1 レビューで確定)
 *
 * 「入力 (Step1) → AI の答えを確かめる (Step2) → 音声を作って詰める (Step3)」という
 * 利用者の思考順に合わせて 3 段に割る。
 * Step2 を「改善案の確認・編集」に充てることで、**音声合成 (課金) が Step3 に一本化**され、
 * 「テキストを固めてから音を作る」という手戻りの少ない流れになる。
 * 候補カードの SSML 編集 / Audio 再作成は Step3 の候補カード内に内包し、独立 Step にしない。
 *
 * ## 画面構成の方針 (計画 8.2.1 / UI/UX 改善 Phase2)
 *
 * ```
 * ┌ Step1 入力・AI 解析 ┐ ┌ Step2 改善案の確認・編集 ┐   ← 上段 (横並び)
 * │                      │ │  … [Audio サンプル作成]  │
 * └──────────────────────┘ └──────────────────────────┘
 * ┌ Step3 Audio サンプル ─────────────────────────────┐   ← 下段 (全幅)
 * └───────────────────────────────────────────────────┘
 * ```
 *
 * - 横長モニタで使いやすいよう、Step1 / Step2 を上段で横並びにし、Step3 を下段に置く。
 *   画面幅が狭い場合は CSS (grid) で自然に縦積みへ戻る。
 * - 「Audio サンプル作成」ボタンは **Step2 の最下部** に置く
 *   (改善案確認 → Audio サンプル作成の流れ)。処理と状態は `Step3SamplePanel` が
 *   持ったまま、ボタンだけを Portal で Step2 の差し込み口へ描画する。
 * - 単一ページのままとし、Step3 を独立画面にしない。
 * - SSML の構文リファレンス (`/ssml-readme`) は **別タブ** で開く。
 *   デュアルモニター運用を想定しており、本画面の状態には依存させない。
 * - 履歴 (v1/v2/v3) は MVP では保持しないため、期待ギャップを防ぐ注記を常時表示する
 *   (計画のリスク R9: 「リロードしたら消えた」というクレームを先に防ぐ)。
 *   UI/UX 改善 Phase4 で専用行をやめ、タイトル横のエンジン情報の末尾へ統合した。
 *
 * ## API の受け取り方
 *
 * ページは `fetch` を直接呼ばず、`api/ssmlTuning.ts` のクライアントを生成して
 * **関数として子へ注入** する (既存 `ttsImprovement.ts` の「Page は AI を知らない」流儀)。
 * テストでは `analyze` を差し替えられるため、ネットワークなしで通しの操作を検証できる。
 */
import { useMemo, useState } from 'react';
import type React from 'react';

import { BroadcastPointsClient } from '../api/broadcastPoints';
import { SsmlTuningClient } from '../api/ssmlTuning';
import type { SsmlTuningLogRequest, SsmlTuningSamplesResponse } from '../api/ssmlTuning';
import { useAuth } from '../auth/useAuth';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  RESET_BUTTON_LABEL,
  RESET_CONFIRM_MESSAGE,
  SSML_TUNING_LANGUAGE_KEY,
  SSML_TUNING_VOICE_ID,
  Step1AnalyzePanel,
  Step3SamplePanel,
} from '../components/ssmlTuning';
import type { ApplyDictionaryFn } from '../components/ssmlTuning';
import type { PreviewAudioFn } from '../components/ssmlTuning/useSsmlAudio';
import type { AppConfig } from '../config';
import { findPollyLanguage } from '../data/pollyVoices';

/**
 * タイトル横に出す音声エンジン情報 (例: `Amazon Polly / Takumi / 日本語`)。
 *
 * ⚠️ 話者・言語は `SSML_TUNING_VOICE_ID` / `SSML_TUNING_LANGUAGE_KEY` から組み立てる
 * (文字列を直書きすると、実際に合成へ使う値と表示がずれ得る)。
 */
const ENGINE_INFO_PARTS: readonly string[] = [
  'Amazon Polly',
  SSML_TUNING_VOICE_ID,
  findPollyLanguage(SSML_TUNING_LANGUAGE_KEY)?.label ?? SSML_TUNING_LANGUAGE_KEY,
];

/**
 * 「履歴は保持されません」の注記 (計画のリスク R9)。
 *
 * UI/UX 改善 Phase4 で専用の注記行を廃止し、タイトル横のエンジン情報の末尾へ統合した
 * (`(Amazon Polly / Takumi / 日本語 / 履歴は保持されません)`)。縦 1 行ぶんを入力領域へ回す。
 * 詳細 (再読み込みで消える) はホバー (title) とスクリーンリーダー向けの文面で補う。
 */
const NO_HISTORY_LABEL = '履歴は保持されません';
const NO_HISTORY_DETAIL = 'ページを再読み込みすると入力内容と作成した音声は消えます。';

export interface SsmlTuningPageProps {
  /** API 呼び出しに使う設定 (`apiUrl` の取得元)。 */
  config: AppConfig;
  /**
   * Audio サンプル生成を実行する関数 (テスト用の差し替え口)。
   *
   * 省略時は `config` と Cognito ID トークンから `SsmlTuningClient` を生成して使う。
   * ⚠️ 本番コードからは渡さない。テストが実 API を叩かないようにするためだけの引数。
   */
  createSamplesOverride?: (params: { text: string }) => Promise<SsmlTuningSamplesResponse>;
  /**
   * 音声合成を実行する関数 (テスト用の差し替え口)。
   *
   * 省略時は `BroadcastPointsClient.previewAudio()` (既存 `POST /audio-preview`)
   * を使う。**API は無改変で流用** する (計画 8.3.4)。
   * ⚠️ 本番コードからは渡さない。テストが Polly を呼ばないようにするためだけの引数。
   */
  previewAudioOverride?: PreviewAudioFn;
  /**
   * 辞書適用 SSML の作成を実行する関数 (テスト用の差し替え口 / SBI-003)。
   *
   * 省略時は `BroadcastPointsClient.previewAudio()` (既存 `POST /audio-preview`) に
   * **辞書適用を有効にして** `content` を渡し、応答の `applied_ssml` を使う。
   * ⚠️ 本番コードからは渡さない。テストが Polly を呼ばないようにするためだけの引数。
   */
  applyDictionaryOverride?: ApplyDictionaryFn;
  /**
   * 利用ログの記録関数 (テスト用の差し替え口)。
   *
   * 省略時は `SsmlTuningClient.recordLog` (= `POST /ssml-tuning/logs`) を使う。
   * ⚠️ 本番コードからは渡さない。
   *
   * ⚠️ テストで渡す実装も **throw しないこと**。ログは Readme 改善の材料で
   * あって業務データではないため、失敗しても画面の操作を止めない
   * (`Step3SamplePanel` 側でも握り潰しているが、意図を揃えておく)。
   */
  recordLogOverride?: (entry: SsmlTuningLogRequest) => void;
}

export function SsmlTuningPage({
  config,
  createSamplesOverride,
  previewAudioOverride,
  applyDictionaryOverride,
  recordLogOverride,
}: SsmlTuningPageProps): React.JSX.Element {
  const { getIdToken } = useAuth();
  /**
   * Step1 で入力した放送テキスト。
   *
   * Step2 の確認表示・Step3 の入力・ダウンロード名の元になるため、
   * ページ側で保持して Step1 / Step3 で共有する (SBI-001)。
   */
  const [text, setText] = useState('');
  /**
   * Step2 最下部の差し込み口 (「Audio サンプル作成」ボタンの描画先)。
   *
   * ⚠️ `useRef` ではなく state で持つ。ref だと要素がマウントされても再レンダーが
   * 起きず、Step3 側が差し込み先を受け取れないまま (= ボタンが出ない) になる。
   * callback ref に `setState` を渡せば、マウント時に 1 回だけ再レンダーされる。
   */
  const [step2Actions, setStep2Actions] = useState<HTMLDivElement | null>(null);
  /**
   * 「リセット」の世代番号 (Step1〜Step3 のパネルの `key` に使う)。
   *
   * ## リセットの方式
   *
   * 各パネルの state を 1 つずつ空に戻すのではなく、**key を変えて作り直す**。
   *   - Step1: 放送テキスト (ページが保持)
   *   - Step2: 確認表示 (ページの放送テキストから導出)
   *   - Step3: 候補一覧 / SSML 編集 / 音声 / エラー / 生成状態 / 進捗
   * を初期値へ戻す処理を個別に書くと、今後 state を足したときに
   * **リセット漏れ (前回の検証内容が混ざる)** が起きやすい。作り直せば
   * 初期表示と同じ状態になることが構造的に保証される。
   *
   * ⚠️ 音声の blob URL は `useSsmlAudio` のアンマウント処理で revoke される
   * (作り直しで解放漏れは起きない)。処理中の解析・生成の応答は各パネルが
   * アンマウント後に捨てる (リセット後に前回の結果が復活しないように)。
   *
   * ⚠️ リセットは **画面上の作業内容のみ** が対象。API は一切呼ばず、
   * README・サーバ側データ・マスタデータ等の永続データには触れない。
   */
  const [resetGeneration, setResetGeneration] = useState(0);
  /** リセット確認ダイアログの表示状態。 */
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

  /** 確認ダイアログで OK が押されたときに、画面を初期表示状態へ戻す。 */
  function handleResetConfirm(): void {
    setIsResetConfirmOpen(false);
    // ページが持つ Step 間の受け渡し値も初期値へ戻す (パネルの作り直しと同時に)。
    setText('');
    setResetGeneration((prev) => prev + 1);
  }

  // クライアントは config / 認証が変わらない限り作り直さない。
  // 毎レンダーで生成すると子コンポーネントの props が毎回変わってしまう。
  const createSamples = useMemo(() => {
    if (createSamplesOverride) {
      return createSamplesOverride;
    }
    const client = new SsmlTuningClient({ config, getIdToken });
    return (params: { text: string }) => client.createSamples(params);
  }, [createSamplesOverride, config, getIdToken]);

  /*
   * 音声合成は **既存の放送地点プレビュー API** をそのまま使う (計画 8.3.4)。
   * 専用の合成 API を新設しないのは、`POST /audio-preview` が既に
   * SSML / voice_id / language を受け取り、SSML 構文エラーを 400 に変換する
   * 経路を持っているため (二重実装すると挙動が分岐する)。
   */
  const previewAudio = useMemo<PreviewAudioFn>(() => {
    if (previewAudioOverride) {
      return previewAudioOverride;
    }
    const client = new BroadcastPointsClient({ config, getIdToken });
    // SBI-03: チューニング画面は利用者 / AI が組み立てた SSML を **そのまま** 聴き比べる
    // 画面なので、SSML 辞書は適用しない (辞書で置換されると「SSML の違い」と
    // 「辞書の違い」が混ざり、改善案どうしの比較にならない)。
    return (payload) => client.previewAudio({ ...payload, applyDictionary: false });
  }, [previewAudioOverride, config, getIdToken]);

  /*
   * 辞書適用 SSML の作成 (SBI-003「辞書適用Audio作成」)。
   *
   * 辞書の適用ロジックはサーバ (`lambda/common/ssml_dictionary.py`) が正源なので、
   * 画面で再実装せず **既存の `POST /audio-preview` を無改変で使う**。
   * `content` (free_text / SSML 入力) を渡すと、応答に辞書適用後の `applied_ssml` が付く
   * (辞書が 1 件以上効いたときのみ。効かなければ置換前の `rendered_ssml` を使う)。
   *
   * ⚠️ 上の `previewAudio` と違い、ここは **`applyDictionary: false` にしない**
   * (辞書適用結果を得るのが目的)。サーバは応答の音声も合成するが、ここでは使わない。
   * Step2 の音声 (SBI-005) は、適用済みの SSML に辞書を二重適用しないよう、
   * 上の `previewAudio` (`applyDictionary: false`) で別途合成する。
   */
  const applyDictionary = useMemo<ApplyDictionaryFn>(() => {
    if (applyDictionaryOverride) {
      return applyDictionaryOverride;
    }
    const client = new BroadcastPointsClient({ config, getIdToken });
    return (payload) =>
      client.previewAudio({
        voiceId: payload.voiceId,
        language: payload.language,
        content: {
          category: 'free_text',
          properties: { input_mode: 'ssml', ssml: payload.ssml },
        },
      });
  }, [applyDictionaryOverride, config, getIdToken]);

  /*
   * 利用ログ (`POST /ssml-tuning/logs`) の送信関数。
   *
   * ⚠️ **`void` で投げっぱなしにする (await しない)**。
   * ダウンロードは押した瞬間にブラウザが保存を始めるため、await すると
   * 「ログの完了を待って保存が遅れる」ことになる。ログは Readme 改善の
   * 材料であって業務データではないので、順序も完了も保証しない。
   *
   * ⚠️ `recordLog` は内部で例外を握り潰すため、ここで catch は要らない
   * (それでも `void` を明示するのは、Promise を捨てていることを
   *  読み手と ESLint の双方に示すため)。
   */
  const recordLog = useMemo(() => {
    if (recordLogOverride) {
      return recordLogOverride;
    }
    const client = new SsmlTuningClient({ config, getIdToken });
    return (entry: SsmlTuningLogRequest) => {
      void client.recordLog(entry);
    };
  }, [recordLogOverride, config, getIdToken]);

  return (
    <div className="ssml-tuning-page" data-testid="ssml-tuning-page">
      {/*
        タイトル行 (UI/UX 改善 Phase1):
          [SSML チューニング (Amazon Polly / Takumi / 日本語)] ........ [SSML Readme を別タブで開く]
        狭い画面では flex-wrap でリンクが次行へ回り込む (右寄せは維持)。
      */}
      <header className="ssml-tuning-page__header">
        <div className="ssml-tuning-page__title-group">
          <h2 className="ssml-tuning-page__title">SSML チューニング</h2>
          {/*
            見出しの一部にはしない (h2 のアクセシブルネームを「SSML チューニング」に保つ)。
            ⚠️ 選択 UI ではなく固定値の表示 (Provider / Voice 選択は作らない / 計画 8 章)。
          */}
          <span className="ssml-tuning-page__engine" data-testid="ssml-tuning-engine-info">
            ({ENGINE_INFO_PARTS.join(' / ')} /{' '}
            {/*
              R9: 「リロードしたら消えた」という期待ギャップを先に伝える。
              専用行は設けず (縦幅削減)、エンジン情報の末尾へ統合する。
            */}
            <span
              className="ssml-tuning-page__no-history"
              data-testid="ssml-tuning-no-history-notice"
              title={NO_HISTORY_DETAIL}
            >
              {NO_HISTORY_LABEL}
              <span className="visually-hidden">。{NO_HISTORY_DETAIL}</span>
            </span>
            )
          </span>
        </div>
        {/*
          別タブで開く: 本画面の入力状態に影響を与えず、
          デュアルモニターで構文リファレンスを並べて参照できるようにする (計画 8.2.1)。
        */}
        <a
          className="ssml-tuning-page__readme-link"
          href="/ssml-readme"
          target="_blank"
          rel="noreferrer"
          data-testid="ssml-tuning-readme-link"
        >
          SSML Readme を別タブで開く
        </a>
      </header>

      {/*
        上段: Step1 (入力・AI 解析) と Step2 (改善案の確認・編集) を横並びにする。
        `Step1AnalyzePanel` は 2 つの section を返すため、それぞれが grid の 1 列になる。
      */}
      <div className="ssml-tuning-page__top" data-testid="ssml-tuning-top-row">
        <Step1AnalyzePanel
          key={`step1-${resetGeneration}`}
          text={text}
          onTextChange={setText}
          /* SBI-002: Step1 の放送テキストをそのまま Polly へ渡して試聴・再作成・ダウンロードする。 */
          previewAudio={previewAudio}
          /* SBI-003: 「辞書適用Audio作成」で放送テキストに SSML 辞書を適用し、Step2 へ表示する。 */
          applyDictionary={applyDictionary}
          step2ActionsRef={setStep2Actions}
          /*
            「リセット」は Step1 のタイトル行右側に出すが、対象は Step1〜Step3 の全体。
            確認ダイアログとクリア処理はページが担う (Step3 の状態はパネルの外にあるため)。
          */
          onResetRequest={() => setIsResetConfirmOpen(true)}
        />
      </div>

      {/*
        下段: Step3 (Audio サンプル 最大 5 候補 + 各候補の音声)。
        入力は Step1 の放送テキスト (SBI-001 で AI 改善案を廃止したため、暫定でそのまま渡す)。
        未入力時は Step3 側で「Step1 で入力してください」と案内させる。
      */}
      <Step3SamplePanel
        key={`step3-${resetGeneration}`}
        sourceText={text}
        createSamples={createSamples}
        previewAudio={previewAudio}
        /*
          ダウンロードのファイル名に使う放送テキスト (`遅延案内_駅名強調.mp3` の前半)。
          ⚠️ 後続 SBI で `sourceText` が辞書適用済みの文面になっても保存名が変わらないよう、
          利用者が入力した原文を別項目で渡す。
        */
        broadcastText={text}
        /*
          利用ログの記録 (docs/ssml-tuning-usage-log.md)。

          ⚠️ 目的は **Readme 改善の材料収集** であって利用者の監視ではない。
          そのため Cognito の情報 (sub / メール) は一切渡さない
          (`useAuth()` から取れるが、`getIdToken` を API 認証以外に使わない)。
        */
        recordLog={recordLog}
        /*
          「Audio サンプル作成」ボタンを Step2 最下部へ描画する (UI/UX 改善 Phase2)。
          ⚠️ 初回レンダーでは `null` (差し込み口が未マウント) で、直後に要素が入る。
        */
        actionsContainer={step2Actions}
      />

      {/*
        リセットの確認ダイアログ。window.confirm ではなく共通の ConfirmDialog を使う
        (ブラウザが付けるホスト名ヘッダーを出さない / 他画面と見た目を揃える)。
        キャンセル時は何もしない (入力内容はそのまま残る)。
      */}
      <ConfirmDialog
        open={isResetConfirmOpen}
        title={RESET_BUTTON_LABEL}
        message={RESET_CONFIRM_MESSAGE}
        confirmLabel="OK"
        destructive
        onConfirm={handleResetConfirm}
        onCancel={() => setIsResetConfirmOpen(false)}
      />
    </div>
  );
}
